/**
 * SIP-Phone backend client — 通信录 / 通话记录 / 即时消息 / 群聊 / 会议
 *
 * 全部走 /v1/sip-phone/* (axios http instance 已带 Bearer token).
 */
import { http } from './client.js';
import type { SipGlobalConfig } from './sip-config.js';

export interface DirectoryUser {
  id: string;
  name: string;
  nameEn?: string | null;
  domainName: string;
  email: string | null;
  mobile: string | null;
  extension: string;
  jobTitle: string | null;
  businessUnitId: string | null;
  kind: 'user';
}

export interface DirectoryContact {
  id: string;
  name: string;
  email: string | null;
  mobile: string | null;
  jobTitle: string | null;
  account: { id: string; name: string | null } | null;
  kind: 'contact';
}

export interface CallLogEntry {
  id: string;
  direction: 'outbound' | 'inbound';
  localExtension: string;
  remoteNumber: string;
  remoteName: string | null;
  remoteUserId: string | null;
  remoteContactId: string | null;
  startAt: string;
  endedAt: string | null;
  durationSec: number | null;
  status: 'completed' | 'missed' | 'failed' | 'cancelled' | 'in-progress';
  failureReason: string | null;
}

export interface Conversation {
  id: string;
  type: 'p2p' | 'group' | 'conference';
  name: string | null;
  memberIds: string[];
  members?: Array<{
    id: string;
    name: string;
    domainName?: string | null;
    email?: string | null;
    extension?: string | null;
  }>;
  roomCode: string | null;
  createdBy: string | null;
  createdOn: string;
  modifiedOn: string;
  isArchived: boolean;
  lastMessage?: {
    body?: string;
    sentAt?: string;
    senderId?: string | null;
    msgType?: string;
  } | null;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  senderId: string | null;
  senderName: string;
  body: string;
  msgType: 'text' | 'system' | 'call_invite' | 'call_event';
  sentAt: string;
}

export interface SipCallingAssignment {
  userId: string;
  extension: string;
  enabled: boolean;
  modifiedOn: string;
  user: {
    fullname: string | null;
    domainname: string | null;
    internalemailaddress: string | null;
    isdisabled: boolean | null;
  };
}

export const sipPhoneApi = {
  getCallingConfig: () =>
    http.get<SipGlobalConfig>('/sip-phone/calling/config').then((r) => r.data),
  updateCallingConfig: (body: Partial<SipGlobalConfig>) =>
    http.patch<SipGlobalConfig>('/sip-phone/calling/config', body).then((r) => r.data),
  getMyCallingAssignment: () =>
    http
      .get<{ enabled: boolean; extension: string | null }>('/sip-phone/calling/me')
      .then((r) => r.data),
  listCallingAssignments: () =>
    http.get<SipCallingAssignment[]>('/sip-phone/calling/assignments').then((r) => r.data),
  saveCallingAssignment: (body: { userId: string; extension: string; enabled?: boolean }) =>
    http.post<SipCallingAssignment>('/sip-phone/calling/assignments', body).then((r) => r.data),
  updateCallingAssignment: (userId: string, body: { extension?: string; enabled?: boolean }) =>
    http
      .patch<SipCallingAssignment>(`/sip-phone/calling/assignments/${userId}`, body)
      .then((r) => r.data),
  deleteCallingAssignment: (userId: string) =>
    http.delete<{ ok: boolean }>(`/sip-phone/calling/assignments/${userId}`).then((r) => r.data),

  listEnterprise: (q?: string) =>
    http.get<DirectoryUser[]>('/sip-phone/directory/users', { params: { q } }).then((r) => r.data),
  listContacts: (q?: string) =>
    http
      .get<DirectoryContact[]>('/sip-phone/directory/contacts', { params: { q } })
      .then((r) => r.data),

  listCalls: (limit = 50, offset = 0) =>
    http
      .get<CallLogEntry[]>('/sip-phone/call-log', { params: { limit, offset } })
      .then((r) => r.data),
  logCall: (body: {
    direction: 'outbound' | 'inbound';
    localExtension: string;
    remoteNumber: string;
    remoteName?: string;
    remoteUserId?: string;
    remoteContactId?: string;
    startAt?: string;
    endedAt?: string;
    durationSec?: number;
    status: 'completed' | 'missed' | 'failed' | 'cancelled' | 'in-progress';
    failureReason?: string;
  }) => http.post<CallLogEntry>('/sip-phone/call-log', body).then((r) => r.data),
  deleteCall: (id: string) => http.delete(`/sip-phone/call-log/${id}`).then((r) => r.data),

  listConversations: () => http.get<Conversation[]>('/sip-phone/conversations').then((r) => r.data),
  startP2p: (peerUserId: string) =>
    http.post<Conversation>('/sip-phone/conversations/p2p', { peerUserId }).then((r) => r.data),
  createGroup: (name: string, memberIds: string[]) =>
    http
      .post<Conversation>('/sip-phone/conversations/group', { name, memberIds })
      .then((r) => r.data),
  addMember: (conversationId: string, memberId: string) =>
    http
      .post<Conversation>(`/sip-phone/conversations/${conversationId}/members`, { memberId })
      .then((r) => r.data),
  leaveConversation: (conversationId: string) =>
    http
      .post<{ ok: boolean }>(`/sip-phone/conversations/${conversationId}/leave`, {})
      .then((r) => r.data),

  createConference: (name: string, memberIds: string[] = []) =>
    http.post<Conversation>('/sip-phone/conferences', { name, memberIds }).then((r) => r.data),
  joinConference: (identifier: string) =>
    http.post<Conversation>('/sip-phone/conferences/join', { identifier }).then((r) => r.data),

  listMessages: (conversationId: string, limit = 100) =>
    http
      .get<ChatMessage[]>(`/sip-phone/conversations/${conversationId}/messages`, {
        params: { limit },
      })
      .then((r) => r.data),
  sendMessage: (conversationId: string, body: string) =>
    http
      .post<ChatMessage>(`/sip-phone/conversations/${conversationId}/messages`, { body })
      .then((r) => r.data),
};
